/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import useWfApi from './axios/useWfApi';

export interface WfInformationStatusParametersBody {
  disasterId: string;
  productNumber: string;
  branchCode: string;
  responseInput: string;
  floodRequired: boolean;
  floodCategory: string;
  floodLocation: string;
  drainageRequired: boolean;
  pitDepth: number;
  waterDepth: number;
  pitWidth: number;
  pitLength: number;
  priority: number;
  estimatedWorkTime: number;
  floodComments: string;
  workSubmissionStatus: string;
  workRemarks: string;
  partsRequired: boolean;
  partsNumber: string;
  partsName: string;
  quantity: string;
  arrangement: string;
  deliveryAddress: string;
  estimatedArrivalTime: string;
  requestNumber: string;
  workOrderNumber: string;
  partsComments: string;
}

export interface WfFloodPhotoParameters {
  disasterId: string;
  branchCode: string;
  productNumber: string;
  photoData: File | Blob;
  fileName: string;
}

export interface WfPartsPhotoParameters {
  disasterId: string;
  branchCode: string;
  productNumber: string;
  photoData: File | Blob;
  fileName: string;
}

function useWfRecoveryRepo() {
  const { post, get, del, postVoid } = useWfApi();

  const postWorkplaceInformationStatusRequest = useCallback(
    async (body: WfInformationStatusParametersBody) => {
      return await postVoid('/api/wf/recovery/register', body);
    },
    [postVoid]
  );

  const postFloodRecoveryPhoto = useCallback(
    async (params: WfFloodPhotoParameters) => {
      try {
        const formData = new FormData();
        formData.append('disasterId', params.disasterId);
        formData.append('branchCode', params.branchCode);
        formData.append('productNumber', params.productNumber);
        formData.append('photoData', params.photoData);
        formData.append('fileName', params.fileName);

        return await post<string>('/api/wf/recoveries/photo/flood', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
      } catch (error) {
        console.error('Error uploading flood photo:', error);
        return null;
      }
    },
    [post]
  );

  const postPartsRecoveryPhoto = useCallback(
    async (params: WfPartsPhotoParameters) => {
      try {
        const formData = new FormData();
        formData.append('disasterId', params.disasterId);
        formData.append('branchCode', params.branchCode);
        formData.append('productNumber', params.productNumber);
        formData.append('photoData', params.photoData);
        formData.append('fileName', params.fileName);

        return await post<string>('/api/wf/recoveries/photo/parts', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
      } catch (error) {
        console.error('Error uploading parts photo:', error);
        return null;
      }
    },
    [post]
  );

  const getFloodRecoveryPhoto = useCallback(
    async (photoId: string) => {
      try {
        return await get<string>('/api/wf/recoveries/photo/flood', {
          params: { photoId },
        });
      } catch (error) {
        console.error('Error fetching flood photo:', error);
        return null;
      }
    },
    [get]
  );

  const getPartsRecoveryPhoto = useCallback(
    async (photoId: string) => {
      try {
        return await get<string>('/api/wf/recoveries/photo/parts', {
          params: { photoId },
        });
      } catch (error) {
        console.error('Error fetching parts photo:', error);
        return null;
      }
    },
    [get]
  );

  const deleteFloodRecoveryPhoto = useCallback(
    async (photoId: string) => {
      try {
        const res = await del<any>('/api/wf/recoveries/photo/flood', {
          params: { photoId },
        });
        return res !== null;
      } catch (error) {
        console.error('Error deleting flood photo:', error);
        return false;
      }
    },
    [del]
  );

  const deletePartsRecoveryPhoto = useCallback(
    async (photoId: string) => {
      try {
        const res = await del<any>('/api/wf/recoveries/photo/parts', {
          params: { photoId },
        });
        return res !== null;
      } catch (error) {
        console.error('Error deleting parts photo:', error);
        return false;
      }
    },
    [del]
  );

  return {
    postWorkplaceInformationStatusRequest,
    postFloodRecoveryPhoto,
    getFloodRecoveryPhoto,
    deleteFloodRecoveryPhoto,
    postPartsRecoveryPhoto,
    getPartsRecoveryPhoto,
    deletePartsRecoveryPhoto,
  };
}

export default useWfRecoveryRepo;
